import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { NurseSequenceReorderRequest } from "@/api/nurses";
import { isDispatchedDisplayRow } from "@/components/feature/nurse-membership/splitDispatchedRows";
import { toast } from "@/components/toast/CustomToast";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type {
  InboundInfo,
  InboundItem,
} from "@/hooks/roster-craete/useRosterSchedule";
import type { NurseResponse, NurseWithRelation } from "@/types/nurse-type";
import {
  nurseGroupSortKey,
  sortNurseListByRoleThenSequence,
} from "@/utils/common-util";
import {
  enqueueRosterNurseSequenceSave,
  flushRosterNurseSequenceOnPageHide,
  persistRosterNurseSequenceSave,
  replayPersistedRosterNurseSequenceSave,
  waitForRosterNurseSequenceQueue,
} from "@/pages/roster-create/services/rosterNurseSequenceSaveQueue";

type Direction = "up" | "down";
type ListKind = "active" | "inactive";

type SequenceRow = {
  readonly nurse_id: string;
  readonly role?: string | null;
  readonly active?: unknown;
  readonly sequence?: number | null;
  readonly is_inbound?: boolean;
  readonly inbound?: InboundInfo | readonly InboundItem[] | null;
  readonly assignments?: readonly InboundItem[] | null;
};

type SequenceNurse = NurseWithRelation & {
  readonly is_inbound?: boolean;
};

type PendingSave = {
  readonly revision: number;
  readonly payload: NurseSequenceReorderRequest;
  readonly pendingIds: readonly string[];
};

type UseNurseSequenceReorderOptions = {
  readonly nurses: readonly SequenceNurse[];
  readonly selectedGroupId?: string | null;
  readonly listGroupId?: string | null;
  readonly fullSchemaGroupId?: string | null;
  readonly debounceMs?: number;
  readonly onManualSortReset?: () => void;
};

type UseNurseSequenceReorderResult = {
  readonly moveNurse: (
    nurseId: string,
    direction: Direction,
    listKind: ListKind,
    visibleNurseIds: readonly string[]
  ) => void;
  readonly isSavingSequence: boolean;
  readonly pendingSequenceNurseIds: ReadonlySet<string>;
};

const REORDER_SAVE_IDLE_MS = 1200;

function isInactiveRow(row: { readonly active?: unknown }): boolean {
  return row.active === 0 || row.active === "0" || row.active === false;
}

function isBlockedRow(row: SequenceRow): boolean {
  return (
    row.nurse_id.startsWith("temp_") ||
    isDispatchedDisplayRow(row) ||
    row.nurse_id.trim() === ""
  );
}

function replaceSequenceForCache<T extends SequenceRow>(
  rows: readonly T[] | undefined,
  nextRows: readonly SequenceRow[]
): T[] | undefined {
  if (!rows) return rows;

  const sequenceById = new Map(
    nextRows.map((row) => [row.nurse_id, row.sequence ?? 0])
  );

  return sortNurseListByRoleThenSequence(
    rows.map((row) => {
      const sequence = sequenceById.get(row.nurse_id);
      return sequence == null ? row : { ...row, sequence };
    })
  );
}

function buildReorderPayload(
  rows: readonly SequenceNurse[]
): NurseSequenceReorderRequest {
  const active_order: string[] = [];
  const inactive_order: string[] = [];

  sortNurseListByRoleThenSequence([...rows]).forEach((row) => {
    if (isBlockedRow(row)) return;
    if (isInactiveRow(row)) {
      inactive_order.push(row.nurse_id);
      return;
    }
    active_order.push(row.nurse_id);
  });

  return { active_order, inactive_order };
}

function swapRows(
  rows: readonly SequenceNurse[],
  sourceId: string,
  targetId: string
): SequenceNurse[] | null {
  const source = rows.find((row) => row.nurse_id === sourceId);
  const target = rows.find((row) => row.nurse_id === targetId);
  if (!source || !target) return null;
  if (isBlockedRow(source) || isBlockedRow(target)) return null;
  if (nurseGroupSortKey(source) !== nurseGroupSortKey(target)) return null;

  const sourceSequence = Number(source.sequence ?? 0);
  const targetSequence = Number(target.sequence ?? 0);

  return sortNurseListByRoleThenSequence(
    rows.map((row) => {
      if (row.nurse_id === sourceId) {
        return { ...row, sequence: targetSequence };
      }
      if (row.nurse_id === targetId) {
        return { ...row, sequence: sourceSequence };
      }
      return row;
    })
  );
}

function findTargetId(
  sourceId: string,
  direction: Direction,
  visibleNurseIds: readonly string[]
): string | null {
  const sourceIndex = visibleNurseIds.indexOf(sourceId);
  if (sourceIndex < 0) return null;
  const targetIndex = direction === "up" ? sourceIndex - 1 : sourceIndex + 1;
  return visibleNurseIds[targetIndex] ?? null;
}

export function useNurseSequenceReorder({
  nurses,
  selectedGroupId,
  listGroupId,
  fullSchemaGroupId,
  debounceMs = REORDER_SAVE_IDLE_MS,
  onManualSortReset,
}: UseNurseSequenceReorderOptions): UseNurseSequenceReorderResult {
  const queryClient = useQueryClient();
  const [isSavingSequence, setIsSavingSequence] = useState(false);
  const [pendingSequenceNurseIds, setPendingSequenceNurseIds] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const latestRowsRef = useRef<readonly SequenceNurse[]>(nurses);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const inFlightRef = useRef(false);
  const isDirtyRef = useRef(false);
  const revisionRef = useRef(0);
  const cleanupFlushedRevisionRef = useRef<number | null>(null);

  useEffect(() => {
    if (isDirtyRef.current) return;
    latestRowsRef.current = nurses;
  }, [nurses]);

  const invalidateSequenceCaches = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.listScope(listGroupId),
      }),
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.fullSchema(fullSchemaGroupId),
      }),
    ]);
  }, [fullSchemaGroupId, listGroupId, queryClient]);

  const patchCaches = useCallback(
    (nextRows: readonly SequenceNurse[]) => {
      queryClient.setQueriesData<NurseResponse[] | undefined>(
        { queryKey: QUERY_KEYS.nurses.listScope(listGroupId) },
        (previous) => replaceSequenceForCache(previous, nextRows)
      );
      queryClient.setQueryData<NurseResponse[] | undefined>(
        QUERY_KEYS.nurses.fullSchema(fullSchemaGroupId),
        (previous) => replaceSequenceForCache(previous, nextRows)
      );
    },
    [fullSchemaGroupId, listGroupId, queryClient]
  );

  const flushSave = useCallback(() => {
    if (inFlightRef.current) return;
    if (debounceTimerRef.current) return;
    const save = pendingSaveRef.current;
    if (!save) return;

    pendingSaveRef.current = null;
    inFlightRef.current = true;

    void (async () => {
      try {
        await enqueueRosterNurseSequenceSave(save.payload, selectedGroupId);
        const hasPendingDebounce = debounceTimerRef.current != null;
        const isLatestSave = save.revision === revisionRef.current;
        if (!pendingSaveRef.current && !hasPendingDebounce && isLatestSave) {
          isDirtyRef.current = false;
          setIsSavingSequence(false);
          setPendingSequenceNurseIds(new Set());
        }
      } catch (error) {
        if (save.revision === revisionRef.current) {
          pendingSaveRef.current = null;
          isDirtyRef.current = false;
          setIsSavingSequence(false);
          setPendingSequenceNurseIds(new Set());
          toast.error("근무자 순서 저장에 실패했습니다.");
          await waitForRosterNurseSequenceQueue(selectedGroupId);
          await invalidateSequenceCaches();
        } else {
          console.error("Stale nurse sequence save failed:", error);
        }
      } finally {
        inFlightRef.current = false;
        if (pendingSaveRef.current && !debounceTimerRef.current) {
          flushSave();
        }
      }
    })();
  }, [invalidateSequenceCaches, selectedGroupId]);

  const scheduleSave = useCallback(
    (save: PendingSave) => {
      pendingSaveRef.current = save;
      isDirtyRef.current = true;
      cleanupFlushedRevisionRef.current = null;
      persistRosterNurseSequenceSave(save.payload, selectedGroupId);
      setIsSavingSequence(true);
      setPendingSequenceNurseIds(new Set(save.pendingIds));

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        flushSave();
      }, debounceMs);
    },
    [debounceMs, flushSave, selectedGroupId]
  );

  const flushPendingSaveOnCleanup = useCallback(() => {
    const save = pendingSaveRef.current;
    if (!save || cleanupFlushedRevisionRef.current === save.revision) return;

    cleanupFlushedRevisionRef.current = save.revision;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const request = flushRosterNurseSequenceOnPageHide(
      save.payload,
      selectedGroupId
    );
    if (!request) return;

    pendingSaveRef.current = null;
    inFlightRef.current = true;
    void request
      .then(() => {
        if (save.revision !== revisionRef.current) return;
        isDirtyRef.current = false;
        setIsSavingSequence(false);
        setPendingSequenceNurseIds(new Set());
      })
      .catch(async (error) => {
        console.error("Nurse sequence lifecycle save failed:", error);
        if (save.revision !== revisionRef.current) return;
        isDirtyRef.current = false;
        setIsSavingSequence(false);
        setPendingSequenceNurseIds(new Set());
        await waitForRosterNurseSequenceQueue(selectedGroupId);
        await invalidateSequenceCaches();
      })
      .finally(() => {
        inFlightRef.current = false;
        if (pendingSaveRef.current && !debounceTimerRef.current) flushSave();
      });
  }, [flushSave, invalidateSequenceCaches, selectedGroupId]);

  const moveNurse = useCallback(
    (
      nurseId: string,
      direction: Direction,
      listKind: ListKind,
      visibleNurseIds: readonly string[]
    ) => {
      const targetId = findTargetId(nurseId, direction, visibleNurseIds);
      if (!targetId) return;

      const rows = latestRowsRef.current;
      const source = rows.find((row) => row.nurse_id === nurseId);
      const target = rows.find((row) => row.nurse_id === targetId);
      if (!source || !target) return;
      if (listKind === "active" && (isInactiveRow(source) || isInactiveRow(target))) {
        return;
      }
      if (listKind === "inactive" && (!isInactiveRow(source) || !isInactiveRow(target))) {
        return;
      }

      const nextRows = swapRows(rows, nurseId, targetId);
      if (!nextRows) return;

      latestRowsRef.current = nextRows;
      onManualSortReset?.();
      patchCaches(nextRows);

      revisionRef.current += 1;
      scheduleSave({
        revision: revisionRef.current,
        payload: buildReorderPayload(nextRows),
        pendingIds: [nurseId, targetId],
      });
    },
    [onManualSortReset, patchCaches, scheduleSave]
  );

  useEffect(() => {
    const handlePageHide = () => {
      flushPendingSaveOnCleanup();
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      flushPendingSaveOnCleanup();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [flushPendingSaveOnCleanup]);

  useEffect(() => {
    void replayPersistedRosterNurseSequenceSave(selectedGroupId)
      .then((replayed) => {
        if (replayed) return invalidateSequenceCaches();
      })
      .catch((error) => {
        console.error("Nurse sequence replay failed:", error);
      });
  }, [invalidateSequenceCaches, selectedGroupId]);

  return {
    moveNurse,
    isSavingSequence,
    pendingSequenceNurseIds,
  };
}
